/**
 * Realtime token provider for the public storefront.
 *
 * The user-scoped realtime connection token is brokered through the BFF
 * (`/api/realtime/token`) — exactly like every other public call — NOT a direct
 * browser→monolith Connect call (which the monolith's staff-only CORS blocked).
 * The browser then opens the WebSocket to the returned `websocketUrl` (a WS is not
 * subject to CORS). With this the public app makes ZERO direct monolith-API
 * calls — enforced by the `no_direct_monolith_client` lint.
 */
import { bff } from '@/shared/lib/bff';
import { clientLogger } from '@/shared/lib/client-logger';
import { tokensFromResponse, type TokenResult } from '@letsgethome/realtime';

/**
 * Mint a realtime token, saying WHY when it cannot.
 *
 * ⛔ THIS USED TO RETURN `RealtimeTokens | null` AND THAT NULL WAS THE WHOLE
 * PROBLEM. Three genuinely different things landed on it — the fetch threw, the
 * BFF answered with a failure envelope, the body was missing a field — and the
 * client could then only report `no subscription token for <channel>`, which
 * names no cause. The distinction did survive in one place, a
 * `clientLogger.warn` below, and that place is unreachable in practice:
 * `clientLogger` routes warn to `console.WARN` (the e2e console assertion
 * captures only errors) and BUFFERS it for a `sendBeacon` flush, which a page
 * navigating every second never performs. Measured across three full
 * `//web:e2e` runs: **154 `GetToken` calls, zero occurrences of either warn
 * string.** The warns are kept — they are useful where a page lives long enough
 * to flush — but they are no longer the only carrier.
 *
 * ⚠ Note what a 200 does NOT prove, because a server log full of them is what
 * sent the first investigation down the wrong path. `get_token_at`
 * (`libs-rust-server/apis/realtime/src/handler/mod.rs`) always returns
 * `connection_token` and `websocket_url`, and signs `subscription_token`
 * whenever the channel authorized — so a 200 **cannot** produce the
 * `malformed` branch. `GetToken: 200` lines next to a client-side failure are
 * therefore a different request, not evidence the failing one succeeded.
 */
export async function fetchRealtimeTokens(channel?: string): Promise<TokenResult> {
  let result;
  try {
    result = await bff('/api/realtime/token', { channel });
  } catch (error) {
    // The request did not complete: offline, an abort (a navigation while the
    // token was in flight), a connection failure. NOTHING was learned about
    // the server, which is exactly what `transport` means.
    const detail = error instanceof Error ? error.message : String(error);
    clientLogger.warn({ component: 'realtime', error: detail }, 'Failed to get realtime tokens');
    return { ok: false, reason: 'transport', detail };
  }

  if (!result.success) {
    // The server answered and refused. Its own words are the detail — the BFF
    // forwards a structured `ServerErrorInfo`, so this is the server's message
    // rather than a status code this layer invented.
    const detail =
      result.error?.message ?? 'the realtime token endpoint returned a failure envelope';
    clientLogger.warn({ component: 'realtime', error: detail }, 'Realtime token request refused');
    return { ok: false, reason: 'rejected', detail };
  }

  // Which fields are REQUIRED depends on what was asked for, and that
  // derivation is shared with the staff provider rather than written twice —
  // the two hand-written copies disagreed, in opposite directions.
  const validated = tokensFromResponse(result.data ?? {}, channel);
  if (!validated.ok) {
    clientLogger.warn(
      { component: 'realtime', error: validated.detail },
      'Missing tokens in realtime token response'
    );
  }
  return validated;
}
